import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import './styles.css';

gsap.registerPlugin(ScrollTrigger, SplitText);

(window as any).gsap = gsap;
(window as any).ScrollTrigger = ScrollTrigger;

ScrollTrigger.config({ limitCallbacks: true });

// =========================
// Types
// =========================
type BaseAnimationType =
    | 'fade'
    | 'slide-up'
    | 'slide-down'
    | 'slide-left'
    | 'slide-right'
    | 'flip-up'
    | 'flip-down'
    | 'flip-left'
    | 'flip-right';

type MaskBase = 'lines' | 'words' | 'chars';
type MaskAnimationType = MaskBase | `${MaskBase}-up` | `${MaskBase}-down`;

type AnimationType = BaseAnimationType | MaskAnimationType;

type MaskSplitType = MaskBase;

interface AnimationProps {
    autoAlpha?: number;
    x?: number;
    y?: number;
    rotationX?: number;
    rotationY?: number;
    transformPerspective?: number;
}

interface StaggerOptions {
    selector?: string;
    scroller?: string | Element | Window;
    start?: string;
    staggerEach?: number;
    staggerEase?: string;
    duration?: number;
    ease?: string;
    batchMax?: number;
    interval?: number;
    staggerDecay?: number;
    respectReducedMotion?: boolean;
}

const defaultOptions: Required<StaggerOptions> = {
    selector: '[data-stagger-item]',
    scroller: window,
    start: 'top 75%',
    staggerEach: 0.08,
    staggerEase: 'power2.in',
    duration: 1,
    ease: 'power2.out',
    batchMax: 10,
    interval: 0.1,
    staggerDecay: 0.85,
    respectReducedMotion: true,
};

// =========================
// Animation Definitions
// =========================
const baseAnimationMap: Record<
    BaseAnimationType,
    { from: AnimationProps; to: AnimationProps }
> = {
    'fade': {
        from: { autoAlpha: 0 },
        to: { autoAlpha: 1 },
    },
    'slide-up': {
        from: { autoAlpha: 0, y: 50 },
        to: { autoAlpha: 1, y: 0 },
    },
    'slide-down': {
        from: { autoAlpha: 0, y: -50 },
        to: { autoAlpha: 1, y: 0 },
    },
    'slide-left': {
        from: { autoAlpha: 0, x: 50 },
        to: { autoAlpha: 1, x: 0 },
    },
    'slide-right': {
        from: { autoAlpha: 0, x: -50 },
        to: { autoAlpha: 1, x: 0 },
    },
    'flip-up': {
        from: { autoAlpha: 0, rotationX: -90, transformPerspective: 600 },
        to: { autoAlpha: 1, rotationX: 0 },
    },
    'flip-down': {
        from: { autoAlpha: 0, rotationX: 90, transformPerspective: 600 },
        to: { autoAlpha: 1, rotationX: 0 },
    },
    'flip-left': {
        from: { autoAlpha: 0, rotationY: 90, transformPerspective: 600 },
        to: { autoAlpha: 1, rotationY: 0 },
    },
    'flip-right': {
        from: { autoAlpha: 0, rotationY: -90, transformPerspective: 600 },
        to: { autoAlpha: 1, rotationY: 0 },
    },
};

const isMaskType = (type: string): type is MaskAnimationType =>
    type.startsWith('lines') || type.startsWith('words') || type.startsWith('chars');

const getMaskDirection = (type: MaskAnimationType) =>
    type.endsWith('-down') ? 'down' : 'up';

const getMaskSplitType = (type: MaskAnimationType): MaskSplitType => {
    if (type.startsWith('words')) return 'words';
    if (type.startsWith('chars')) return 'chars';
    return 'lines';
};


// =========================
// SplitText / Mask Setup
// =========================
type MaskStore = {
    split: SplitText;
    targets: HTMLElement[];
    direction: 'up' | 'down';
};

function createMaskTargets(
    element: HTMLElement,
    direction: 'up' | 'down',
    splitType: MaskSplitType
): MaskStore {
    const split = new SplitText(element, {
        type: splitType,
        linesClass: 'split-line',
    });

    const targets = (split as any)[splitType] as HTMLElement[];
    const yValue = direction === 'up' ? 20 : -20;

    gsap.set(targets, { y: yValue, autoAlpha: 0 });

    return { split, targets, direction };
}


function animateMask(store: MaskStore, config: Required<StaggerOptions>, delay: number) {
    return gsap.to(store.targets, {
        y: 0,
        autoAlpha: 1,
        stagger: { each: config.staggerEach, ease: config.staggerEase },
        duration: config.duration,
        ease: config.ease,
        delay,
    });
}

// =========================
// Main
// =========================
export function initScrollStagger(
    scope: Element | Document = document,
    options: StaggerOptions = {}
) {
    const config = { ...defaultOptions, ...options };

    if (config.respectReducedMotion) {
        const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        if (prefersReduced) {
            // アニメーションをスキップするが、要素は即座に表示する
            const allItems = Array.from(scope.querySelectorAll<HTMLElement>('[data-stagger-item]'));
            gsap.set(allItems, { autoAlpha: 1, x: 0, y: 0, rotationX: 0, rotationY: 0 });
            return;
        }
    }

    // [data-stagger] 親配下の [data-stagger-item] はバッチから除外
    const parents = Array.from(scope.querySelectorAll<HTMLElement>('[data-stagger]'));
    const elements = Array.from(scope.querySelectorAll<HTMLElement>(config.selector))
        .filter(el => !el.closest('[data-stagger]'));

    if (elements.length === 0 && parents.length === 0) return;

    const maskMap = new Map<HTMLElement, MaskStore>();

    const initElementState = (el: HTMLElement, defaultType: AnimationType) => {
        const type = (el.dataset.staggerItem || defaultType) as AnimationType;
        if (isMaskType(type)) {
            maskMap.set(el, createMaskTargets(el, getMaskDirection(type), getMaskSplitType(type)));
        } else {
            const base = baseAnimationMap[type as BaseAnimationType];
            if (base) gsap.set(el, base.from);
        }
    };

    elements.forEach(el => initElementState(el, 'slide-up'));

    parents.forEach((parent) => {
        const defaultType = (parent.dataset.stagger || 'slide-up') as AnimationType;
        parent.querySelectorAll<HTMLElement>('[data-stagger-item]').forEach(child => initElementState(child, defaultType));
    });

    const animatedSet = new Set<HTMLElement>();

    const animateBatch = (batch: Element[], initialDelay = 0) => {
        let delay = initialDelay;

        const groups = new Map<BaseAnimationType, HTMLElement[]>();
        const maskEls: HTMLElement[] = [];

        batch.forEach((node) => {
            const el = node as HTMLElement;
            animatedSet.add(el);
            const type = (el.dataset.staggerItem || 'slide-up') as AnimationType;

            if (isMaskType(type)) {
                maskEls.push(el);
            } else {
                const key = type as BaseAnimationType;
                if (!groups.has(key)) groups.set(key, []);
                groups.get(key)!.push(el);
            }
        });

        groups.forEach((list, type) => {
            const anim = baseAnimationMap[type];
            gsap.to(list, {
                ...anim.to,
                stagger: { each: config.staggerEach, ease: config.staggerEase },
                duration: config.duration,
                ease: config.ease,
                delay,
            });
            delay += list.length * config.staggerEach;
        });

        maskEls.forEach((el) => {
            const store = maskMap.get(el);
            if (!store) return;
            animateMask(store, config, delay);
            delay += store.targets.length * config.staggerEach;
        });
    };

    // =========================
    // 親トリガー（data-stagger）
    // =========================
    const animateChildren = (children: HTMLElement[], defaultType: AnimationType) => {
        let delay = 0;
        let interval = config.staggerEach;
        children.forEach((child) => {
            const type = (child.dataset.staggerItem || defaultType) as AnimationType;
            if (isMaskType(type)) {
                const store = maskMap.get(child);
                if (!store) return;
                animateMask(store, config, delay);
                delay += store.targets.length * interval;
            } else {
                const base = baseAnimationMap[type as BaseAnimationType];
                if (!base) return;
                gsap.to(child, { ...base.to, duration: config.duration, ease: config.ease, delay });
                delay += interval;
            }
            interval *= config.staggerDecay;
        });
    };

    parents.forEach((parent) => {
        const defaultType = (parent.dataset.stagger || 'slide-up') as AnimationType;
        const children = Array.from(parent.querySelectorAll<HTMLElement>('[data-stagger-item]'));
        if (!children.length) return;

        let animated = false;

        ScrollTrigger.create({
            trigger: parent,
            scroller: config.scroller,
            start: config.start,
            onEnter: () => { if (!animated) { animated = true; animateChildren(children, defaultType); } },
        });
    });

    if (elements.length > 0) {
        ScrollTrigger.batch(elements, {
            batchMax: config.batchMax,
            interval: config.interval,
            scroller: config.scroller,
            start: config.start,
            onEnter: (batch) => {
                const unAnimated = batch.filter(el => !animatedSet.has(el as HTMLElement));
                if (unAnimated.length) animateBatch(unAnimated);
            },
        });
    }

    ScrollTrigger.refresh();

    return () => {
        ScrollTrigger.getAll().forEach((t) => t.kill());
        maskMap.forEach((store) => store.split.revert());
        maskMap.clear();
    };
}

export default (scope: Element | Document = document) => {
    initScrollStagger(scope);
};

// =========================
// Vendor init
// =========================
let _initialized = false;
let _cleanup: (() => void) | undefined;

export function initScrollTrigger(): void {
    if (_initialized) return;
    _initialized = true;

    // BFCache 保存直前: stagger-loading 付与 + GSAP インラインスタイルリセット
    window.addEventListener('pagehide', (e: PageTransitionEvent) => {
        if (e.persisted) {
            document.querySelectorAll<HTMLElement>('[data-stagger-item]').forEach(el => {
                el.style.removeProperty('opacity');
                el.style.removeProperty('visibility');
                el.style.removeProperty('transform');
            });
        }
    });

    document.addEventListener('ui:update', () => {
        const loading = document.querySelector<HTMLElement>('[data-loading-duration]');
        if (loading) {
            const duration = parseFloat(loading.dataset.loadingDuration ?? '') || 1;
            const delay = parseFloat(loading.dataset.loadingDelay ?? '') || 1;

            // ページ上部へ移動
            if ('scrollRestoration' in history) {
                history.scrollRestoration = 'manual';
            }
            window.addEventListener('pageshow', () => {
                requestAnimationFrame(() => {
                    window.scrollTo(0, 0);
                });
            });

            gsap.to(loading, {
                autoAlpha: 0,
                duration,
                delay,
                ease: 'power2.out',
                onStart: () => {
                    _cleanup?.();
                    setTimeout(mount, 0);
                },
                onComplete: () => {
                    loading.remove();
                },
            });
        } else {
            _cleanup?.();
            setTimeout(mount, 1000);
        }
    });
}

function mount(): void {
    _cleanup = initScrollStagger() ?? undefined;
}